import { createTheme } from '@mui/material/styles';
import { componentOverrides } from './componentOverrides';

// Modern Color Palette - Centralized for easy management
export const colorPalette = {
  // Light Mode Colors
  light: {
    // Primary colors - Signature vibrant orange
    primary: '#FF6600',
    primaryLight: '#FF8533',
    primaryDark: '#CC5200',
    
    // Secondary colors - Modern dark charcoal accent
    secondary: '#2B2D42',
    secondaryLight: '#4A4E69',
    secondaryDark: '#1A1C29',
    
    // Accent colors - Signature orange
    accent: '#FF6600',
    accentLight: '#FF8533',
    accentDark: '#CC5200',
    
    // Success
    success: '#16a34a',
    successLight: '#4ade80',
    successDark: '#15803d',
    
    // Warning
    warning: '#f59e0b',
    warningLight: '#fbbf24',
    warningDark: '#d97706',
    
    // Error
    error: '#dc2626',
    errorLight: '#ef4444',
    errorDark: '#b91c1c',
    
    // Background and Surface
    background: '#FAF7F2',
    surface: '#ffffff',
    surfaceVariant: '#f1f5f9',
    
    // Text colors
    textPrimary: '#1e293b',
    textSecondary: '#64748b',
    textTertiary: '#94a3b8',
    
    // Borders and dividers
    border: '#e2e8f0',
    divider: '#cbd5e1',
    
    // Sidebar
    sidebarBg: '#ffffff',
    sidebarText: '#334155',
    sidebarTextSecondary: '#94a3b8',
    sidebarHover: '#FFF0E6',
    sidebarActive: '#FFF0E6',
    sidebarActiveBg: '#FFF0E6',
    sidebarActiveText: '#FF6600',
    sidebarDivider: '#e2e8f0',
    
    // Topbar
    topbarBg: '#ffffff',
    topbarText: '#334155',
    topbarBorder: '#e2e8f0',
  },
  
  // Dark Mode Colors
  dark: {
    // Primary colors - Signature vibrant orange
    primary: '#FF6600',
    primaryLight: '#FF8533',
    primaryDark: '#CC5200',
    
    // Secondary colors - Modern dark charcoal accent
    secondary: '#2B2D42',
    secondaryLight: '#4A4E69',
    secondaryDark: '#1A1C29',
    
    // Accent colors - Signature orange
    accent: '#FF8533',
    accentLight: '#FFA366',
    accentDark: '#FF6600',
    
    // Success
    success: '#4ade80',
    successLight: '#86efac',
    successDark: '#16a34a',
    
    // Warning
    warning: '#fbbf24',
    warningLight: '#fcd34d',
    warningDark: '#d97706',
    
    // Error
    error: '#ef4444',
    errorLight: '#f87171',
    errorDark: '#dc2626',
    
    // Background and Surface
    background: '#12161A',
    surface: '#1E242B',
    surfaceVariant: '#334155',
    
    // Text colors
    textPrimary: '#f1f5f9',
    textSecondary: '#cbd5e1',
    textTertiary: '#94a3b8',
    
    // Borders and dividers
    border: '#334155',
    divider: '#475569',
    
    // Sidebar
    sidebarBg: '#1E242B',
    sidebarText: '#e2e8f0',
    sidebarTextSecondary: '#cbd5e1',
    sidebarHover: 'rgba(255,102,0,0.12)',
    sidebarActive: 'rgba(255,102,0,0.18)',
    sidebarActiveBg: 'rgba(255,102,0,0.18)',
    sidebarActiveText: '#FF8533',
    sidebarDivider: '#334155',
    
    // Topbar
    topbarBg: '#1E242B',
    topbarText: '#e2e8f0',
    topbarBorder: '#334155',
  },
};

const typography = {
  fontFamily: "'Poppins', sans-serif",
  h1_fontFamily: "'Poppins', sans-serif",
  label_fontFamily: "'Poppins', sans-serif",
};

export const lightTheme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: colorPalette.light.primary,
      light: colorPalette.light.primaryLight,
      dark: colorPalette.light.primaryDark,
    },
    secondary: {
      main: colorPalette.light.secondary,
      light: colorPalette.light.secondaryLight,
      dark: colorPalette.light.secondaryDark,
    },
    error: {
      main: colorPalette.light.error,
      light: colorPalette.light.errorLight,
      dark: colorPalette.light.errorDark,
    },
    warning: {
      main: colorPalette.light.warning,
      light: colorPalette.light.warningLight,
      dark: colorPalette.light.warningDark,
    },
    success: {
      main: colorPalette.light.success,
      light: colorPalette.light.successLight,
      dark: colorPalette.light.successDark,
    },
    background: {
      default: colorPalette.light.background,
      paper: colorPalette.light.surface,
    },
    text: {
      primary: colorPalette.light.textPrimary,
      secondary: colorPalette.light.textSecondary,
    },
    divider: colorPalette.light.divider,
    sidebar: {
      background: colorPalette.light.sidebarBg,
      text: colorPalette.light.sidebarText,
      textSecondary: colorPalette.light.sidebarTextSecondary,
      divider: colorPalette.light.sidebarDivider,
      hover: colorPalette.light.sidebarHover,
      active: colorPalette.light.sidebarActive,
      menuItem: {
        background: 'transparent',
        text: colorPalette.light.sidebarText,
        textActive: colorPalette.light.sidebarActiveText,
        icon: colorPalette.light.sidebarText,
        iconActive: colorPalette.light.primary,
        backgroundActive: colorPalette.light.sidebarActiveBg,
        backgroundHover: colorPalette.light.sidebarHover,
      },
    },
    topbar: {
      background: colorPalette.light.topbarBg,
      text: colorPalette.light.topbarText,
      textSecondary: colorPalette.light.textSecondary,
      divider: colorPalette.light.topbarBorder,
      hover: colorPalette.light.primary,
      border: colorPalette.light.topbarBorder,
    },
    footer: {
      background: colorPalette.light.surface,
      text: colorPalette.light.textPrimary,
    },
  },
  typography,
  components: componentOverrides,
});

export const darkTheme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: colorPalette.dark.primary,
      light: colorPalette.dark.primaryLight,
      dark: colorPalette.dark.primaryDark,
    },
    secondary: {
      main: colorPalette.dark.secondary,
      light: colorPalette.dark.secondaryLight,
      dark: colorPalette.dark.secondaryDark,
    },
    error: {
      main: colorPalette.dark.error,
      light: colorPalette.dark.errorLight,
      dark: colorPalette.dark.errorDark,
    },
    warning: {
      main: colorPalette.dark.warning,
      light: colorPalette.dark.warningLight,
      dark: colorPalette.dark.warningDark,
    },
    success: {
      main: colorPalette.dark.success,
      light: colorPalette.dark.successLight,
      dark: colorPalette.dark.successDark,
    },
    background: {
      default: colorPalette.dark.background,
      paper: colorPalette.dark.surface,
    },
    text: {
      primary: colorPalette.dark.textPrimary,
      secondary: colorPalette.dark.textSecondary,
    },
    divider: colorPalette.dark.divider,
    sidebar: {
      background: colorPalette.dark.sidebarBg,
      text: colorPalette.dark.sidebarText,
      textSecondary: colorPalette.dark.sidebarTextSecondary,
      divider: colorPalette.dark.sidebarDivider,
      hover: colorPalette.dark.sidebarHover,
      active: colorPalette.dark.sidebarActive,
      menuItem: {
        background: 'transparent',
        text: colorPalette.dark.sidebarText,
        textActive: colorPalette.dark.sidebarActiveText,
        icon: colorPalette.dark.sidebarText,
        iconActive: colorPalette.dark.primary,
        backgroundActive: colorPalette.dark.sidebarActiveBg,
        backgroundHover: colorPalette.dark.sidebarHover,
      },
    },
    topbar: {
      background: colorPalette.dark.topbarBg,
      text: colorPalette.dark.topbarText,
      textSecondary: colorPalette.dark.textSecondary,
      divider: colorPalette.dark.topbarBorder,
      hover: colorPalette.dark.primary,
      border: colorPalette.dark.topbarBorder,
    },
    footer: {
      background: colorPalette.dark.surface,
      text: colorPalette.dark.textPrimary,
    },
  },
  typography,
  components: componentOverrides,
});

